import {
  Chip,
} from "@mui/material";

import {
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BuildIcon from "@mui/icons-material/Build";
import EngineeringIcon from "@mui/icons-material/Engineering";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";

import AppDataTable from "../../../../components/common/AppDataTable";

import { ServiceTicket, ServiceType, TicketStatus } from "../types";

type MaintenanceAccessRole = "MANAGER" | "TECHNICIAN" | "VIEWER";

interface ServiceTicketTableProps {
  rows: ServiceTicket[];

  loading: boolean;

  page: number;

  pageSize: number;

  rowCount: number;

  onPageChange: (page: number) => void;

  onPageSizeChange: (
    pageSize: number
  ) => void;

  onEdit: (
    ticket: ServiceTicket
  ) => void;

  onView: (
    ticket: ServiceTicket
  ) => void;

  onAssign: (
    ticket: ServiceTicket
  ) => void;

  onIssueParts: (
    ticket: ServiceTicket
  ) => void;

  onReturnParts: (
    ticket: ServiceTicket
  ) => void;

  onTimeline: (
    ticket: ServiceTicket
  ) => void;

  onCloseTicket: (
    ticket: ServiceTicket
  ) => void;

  onDelete: (
    ticket: ServiceTicket
  ) => void;

  activeServiceType?: ServiceType;

  onStartWork?: (
    ticket: ServiceTicket
  ) => void;

  onReadyForDeployment?: (
    ticket: ServiceTicket
  ) => void;

  accessRole?: MaintenanceAccessRole;
}

const preventiveStatusLabel: Partial<Record<TicketStatus, string>> = {
  RAISED: "SCHEDULED",
  ASSIGNED: "ASSIGNED",
  IN_PROGRESS: "IN_PROGRESS",
  SPARE_ISSUED: "WAITING_FOR_PARTS",
  WAITING_FOR_PARTS: "WAITING_FOR_PARTS",
  RETURN_PENDING: "RETURN_PENDING",
  RETURN_VERIFIED: "RETURN_VERIFIED",
  COMPLETED: "COMPLETED",
  CLOSED: "COMPLETED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
};

const inHouseStatusLabel: Partial<Record<TicketStatus, string>> = {
  RAISED: "INSPECTION",
  ASSIGNED: "ASSIGNED",
  IN_PROGRESS: "REPAIR_IN_PROGRESS",
  SPARE_ISSUED: "WAITING_FOR_PARTS",
  WAITING_FOR_PARTS: "WAITING_FOR_PARTS",
  RETURN_PENDING: "RETURN_PENDING",
  RETURN_VERIFIED: "RETURN_VERIFIED",
  COMPLETED: "QC_PENDING",
  INVOICE_GENERATED: "READY_FOR_DEPLOYMENT",
  CLOSED: "COMPLETED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
};

const statusLabel = (status: TicketStatus, serviceType?: ServiceType) => {
  if (serviceType === "PREVENTIVE") {
    return preventiveStatusLabel[status] || status;
  }

  if (serviceType === "IN_HOUSE") {
    return inHouseStatusLabel[status] || status;
  }

  return status;
};

const canAssign = (ticket: ServiceTicket) => ticket.status === "RAISED";

const canStart = (ticket: ServiceTicket) => ticket.status === "ASSIGNED";

const canIssue = (ticket: ServiceTicket) =>
  ["ASSIGNED", "IN_PROGRESS", "WAITING_FOR_PARTS"].includes(ticket.status);

const canReturn = (ticket: ServiceTicket) =>
  ticket.status === "COMPLETED";

const canCompletePreventive = (ticket: ServiceTicket) =>
  ["IN_PROGRESS", "SPARE_ISSUED", "WAITING_FOR_PARTS"].includes(ticket.status);

const canCompleteWork = (ticket: ServiceTicket) =>
  ["IN_PROGRESS", "SPARE_ISSUED", "WAITING_FOR_PARTS"].includes(ticket.status);

const canCloseTicket = (ticket: ServiceTicket) =>
  ["COMPLETED", "RETURN_VERIFIED", "INVOICE_GENERATED"].includes(ticket.status);

const canReadyForDeployment = (ticket: ServiceTicket) =>
  ticket.status === "COMPLETED";

const ServiceTicketTable = ({
  rows,
  loading,
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onAssign,
  onIssueParts,
  onReturnParts,
  onTimeline,
  onCloseTicket,
  onDelete,
  activeServiceType = "ON_ROAD",
  onStartWork,
  onReadyForDeployment,
  accessRole = "MANAGER",
}: ServiceTicketTableProps) => {
  const canManage = accessRole === "MANAGER";
  const canTechnicianExecute = accessRole === "TECHNICIAN";

  const buildActions = (ticket: ServiceTicket) => {
    if (activeServiceType === "ON_ROAD") {
      const actions = [];

      if (canManage) {
        actions.push(
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(ticket);
          }}
        />,
        <GridActionsCellItem
          key="assign"
          icon={<EngineeringIcon />}
          label="Assign"
          onClick={(event) => {
            event.stopPropagation();
            onAssign(ticket);
          }}
        />,
        );
      }

      if (canTechnicianExecute && canStart(ticket)) {
        actions.push(
          <GridActionsCellItem
            key="start"
            icon={<BuildIcon />}
            label="Start Work"
            onClick={(event) => {
              event.stopPropagation();
              onStartWork?.(ticket);
            }}
          />
        );
      }

      if (canManage && canIssue(ticket)) {
        actions.push(
          <GridActionsCellItem
            key="issue"
            icon={<BuildIcon />}
            label="Issue Spare"
            onClick={(event) => {
              event.stopPropagation();
              onIssueParts(ticket);
            }}
          />
        );
      }

      if (canTechnicianExecute && canCompleteWork(ticket)) {
        actions.push(
          <GridActionsCellItem
            key="complete-work"
            icon={<CheckCircleIcon />}
            label="Complete Work"
            onClick={(event) => {
              event.stopPropagation();
              onCloseTicket(ticket);
            }}
          />
        );
      }

      if (canTechnicianExecute && canReturn(ticket)) {
        actions.push(
          <GridActionsCellItem
            key="return"
            icon={<ReplayIcon />}
            label="Return Spare"
            onClick={(event) => {
              event.stopPropagation();
              onReturnParts(ticket);
            }}
          />
        );
      }

      actions.push(
        <GridActionsCellItem
          key="timeline"
          icon={<HistoryIcon />}
          label="Timeline"
          onClick={(event) => {
            event.stopPropagation();
            onTimeline(ticket);
          }}
        />,
      );

      if (canManage && canCloseTicket(ticket)) {
        actions.push(
        <GridActionsCellItem
          key="close"
          icon={<CheckCircleIcon />}
          label="Close Ticket"
          onClick={(event) => {
            event.stopPropagation();
            onCloseTicket(ticket);
          }}
        />
        );
      }

      if (canManage) {
        actions.push(
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(ticket);
          }}
        />
        );
      }

      return actions;
    }

    const actions = canManage
      ? [
      <GridActionsCellItem
        key="edit"
        icon={<EditIcon />}
        label="Edit"
        onClick={(event) => {
          event.stopPropagation();
          onEdit(ticket);
        }}
      />,
    ]
      : [];

    if (canManage && canAssign(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="assign"
          icon={<EngineeringIcon />}
          label="Assign Technician"
          onClick={(event) => {
            event.stopPropagation();
            onAssign(ticket);
          }}
        />
      );
    }

    if (canTechnicianExecute && canStart(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="start"
          icon={<BuildIcon />}
          label={activeServiceType === "IN_HOUSE" ? "Start Repair" : "Start Service"}
          onClick={(event) => {
            event.stopPropagation();
            onStartWork?.(ticket);
          }}
        />
      );
    }

    if (canManage && canIssue(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="issue"
          icon={<BuildIcon />}
          label="Issue Spare"
          onClick={(event) => {
            event.stopPropagation();
            onIssueParts(ticket);
          }}
        />
      );
    }

    if (canTechnicianExecute && canReturn(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="return"
          icon={<ReplayIcon />}
          label="Return Spare"
          onClick={(event) => {
            event.stopPropagation();
            onReturnParts(ticket);
          }}
        />
      );
    }

    if (
      canTechnicianExecute &&
      activeServiceType === "PREVENTIVE" &&
      canCompletePreventive(ticket)
    ) {
      actions.push(
        <GridActionsCellItem
          key="complete-preventive"
          icon={<CheckCircleIcon />}
          label="Complete Service"
          onClick={(event) => {
            event.stopPropagation();
            onCloseTicket(ticket);
          }}
        />
      );
    }

    if (
      canTechnicianExecute &&
      activeServiceType === "IN_HOUSE" &&
      canCompleteWork(ticket)
    ) {
      actions.push(
        <GridActionsCellItem
          key="complete-in-house"
          icon={<CheckCircleIcon />}
          label="Complete Work"
          onClick={(event) => {
            event.stopPropagation();
            onCloseTicket(ticket);
          }}
        />
      );
    }

    if (canManage && activeServiceType === "IN_HOUSE" && canReadyForDeployment(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="ready"
          icon={<CheckCircleIcon />}
          label="Mark Ready For Deployment"
          onClick={(event) => {
            event.stopPropagation();
            onReadyForDeployment?.(ticket);
          }}
        />
      );
    }

    if (canManage && canCloseTicket(ticket)) {
      actions.push(
        <GridActionsCellItem
          key="close-ticket"
          icon={<CheckCircleIcon />}
          label="Close Ticket"
          onClick={(event) => {
            event.stopPropagation();
            onCloseTicket(ticket);
          }}
        />
      );
    }

    actions.push(
      <GridActionsCellItem
        key="timeline"
        icon={<HistoryIcon />}
        label="Timeline"
        onClick={(event) => {
          event.stopPropagation();
          onTimeline(ticket);
        }}
      />
    );

    if (canManage) {
      actions.push(
      <GridActionsCellItem
        key="delete"
        icon={<DeleteIcon />}
        label="Delete"
        onClick={(event) => {
          event.stopPropagation();
          onDelete(ticket);
        }}
      />
      );
    }

    return actions;
  };

  const columns: GridColDef[] = [
    {
      field: "ticketNumber",
      headerName: "Ticket",
      flex: 1,
      minWidth: 150,
    },

    {
      field: "vehicleNumber",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 140,
    },

    {
      field: "riderId",
      headerName: "Rider ID",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => params.value || "-",
    },

    {
      field: "chassisNumber",
      headerName: "Chassis",
      flex: 1.3,
      minWidth: 180,
    },

    {
      field: "serviceType",
      headerName: "Service Type",
      width: 160,

      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },

    {
      field: "priority",
      headerName: "Priority",
      width: 140,

      renderCell: (params) => {
        const priority =
          params.value;

        let color:
          | "default"
          | "success"
          | "warning"
          | "error" =
          "default";

        if (priority === "LOW")
          color = "success";

        if (priority === "MEDIUM")
          color = "default";

        if (priority === "HIGH")
          color = "warning";

        if (
          priority ===
          "CRITICAL"
        )
          color = "error";

        return (
          <Chip
            label={priority}
            color={color}
            size="small"
          />
        );
      },
    },

    {
      field:
        "assignedTechnicianName",
      headerName:
        "Technician",
      flex: 1.2,
      minWidth: 180,

      renderCell: (params) =>
        params.value || "-",
    },

    {
      field: "status",
      headerName: "Status",
      width: 180,

      renderCell: (params) => {
        const status =
          params.value;

        let color:
          | "default"
          | "info"
          | "warning"
          | "success"
          | "error" =
          "default";

        switch (status) {
          case "ASSIGNED":
            color = "info";
            break;

          case "IN_PROGRESS":
            color = "warning";
            break;

          case "SPARE_ISSUED":
            color = "info";
            break;

          case "WAITING_FOR_PARTS":
            color = "warning";
            break;

          case "RETURN_PENDING":
            color = "warning";
            break;

          case "RETURN_VERIFIED":
            color = "success";
            break;

          case "COMPLETED":
          case "CLOSED":
            color = "success";
            break;

          case "CANCELLED":
          case "EXPIRED":
            color = "error";
            break;

          default:
            color = "default";
        }

        return (
          <Chip
            label={statusLabel(status, params.row.serviceType)}
            color={color}
            size="small"
          />
        );
      },
    },

    {
      field: "createdAt",
      headerName: "Created",
      width: 180,

      renderCell: (params) =>
        new Date(
          params.value
        ).toLocaleDateString(),
    },

    {
      field: "expiresAt",
      headerName: "Expires At",
      width: 190,
      sortable: false,
      renderCell: (params) => {
        if (params.row.serviceType !== "ON_ROAD" || !params.value) return "-";

        const label = new Date(params.value).toLocaleString([], {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });

        return params.row.status === "EXPIRED" ? `Expired ${label}` : label;
      },
    },

    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 260,

      getActions: (params) => buildActions(params.row),
    },
  ];

  return (
    <AppDataTable
      rows={rows}
      columns={columns}
      loading={loading}
      page={page}
      pageSize={pageSize}
      rowCount={rowCount}
      onPageChange={onPageChange}
      onPageSizeChange={
        onPageSizeChange
      }
      onRowClick={(params) => onView(params.row)}
    />
  );
};

export default ServiceTicketTable;
